import { useEffect, useMemo, type ReactNode } from 'react';
import type { TaskBodyProps } from '../index';
import { advanceWorkflow } from '../../../../services/workQueue';
import { TaskSidebarPanel } from '../TaskSidebarPanel';
import { buildSidebarTabs, REFINEMENT_TABS } from '../taskSidebarTabs';
import { DesignPlanHistory } from '../DesignPlanHistory';
import { PatientExternalLinks } from '../PatientExternalLinks';
import { RefinementRdiPanel } from '../submit-design/RefinementRdiPanel';
import { PanelHeader } from '../design-dx/VersionCard';
import { MAX_HAND_BACK_NOTES } from '../design-dx/handBackNotes';
import { AddNoteButton, WithNotes } from '../final-review/FinalReviewNotesColumn';
import { useDraftNotes } from '../final-review/useDraftNotes';
import {
    ReviewDecisionFooter,
    type ReviewDecisionCopy
} from '../final-review/ReviewDecisionFooter';

const HAND_BACK_ACTION = 'request-refinement-design-revision';
const APPROVE_ACTION = 'approve-refinement-design';
const COPY: ReviewDecisionCopy = {
    source: 'RefinementOrthoReviewTask',
    targetLabel: 'Sends back',
    handBack: 'Request revision',
    handingBack: 'Sending…',
    handBackError: 'Failed to request the revision. Please try again.',
    approve: 'Approve for manufacturing',
    approveError: 'Failed to approve for manufacturing. Please try again.'
};

// Ref-6.1 (V2): ODO validates the refinement design and RDI on the orthodontist's behalf.
export function RefinementOrthoReviewTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { notes, addNote, updateNote, removeNote } = useDraftNotes();

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Built once per case so typing a note doesn't re-render every mounted info tab.
    const infoTabs = useMemo(
        () =>
            buildSidebarTabs(c.id, REFINEMENT_TABS, c.phaseEnteredAt).map((t) => ({
                ...t,
                trailing: true
            })),
        [c.id, c.phaseEnteredAt]
    );

    const addNoteButton = (
        <AddNoteButton onClick={() => addNote()} disabled={notes.length >= MAX_HAND_BACK_NOTES} />
    );
    const withNotes = (content: ReactNode) => (
        <WithNotes
            notes={notes}
            onUpdate={updateNote}
            onRemove={removeNote}
            tabNames="RDI or Submissions"
        >
            {content}
        </WithNotes>
    );

    const tabs = [
        {
            key: 'rdi',
            label: 'RDI',
            content: withNotes(
                <div className="flex-1 min-h-0 flex flex-col font-inter">
                    <PanelHeader title="Latest RDI">{addNoteButton}</PanelHeader>
                    <div className="flex-1 min-h-0 p-3">
                        <RefinementRdiPanel patientId={c.id} since={c.phaseEnteredAt} />
                    </div>
                </div>
            )
        },
        {
            key: 'submissions',
            label: 'Submissions',
            content: withNotes(
                <DesignPlanHistory
                    flush
                    patientId={c.id}
                    title="Refinement design submissions"
                    emptyText="No design submissions yet for this refinement."
                    submitType="submit-refinement-for-design"
                    preApprovedType="refinement-design-pre-approved"
                    since={c.phaseEnteredAt}
                    headerRight={<PatientExternalLinks patientId={c.id} manufacturerOnly />}
                    headerEnd={addNoteButton}
                />
            )
        },
        ...infoTabs
    ];

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <TaskSidebarPanel tabs={tabs} keepMounted flush />
            <ReviewDecisionFooter
                caseId={c.id}
                statusMeta={statusMeta}
                onTaskComplete={onTaskComplete}
                notes={notes}
                handBackAction={HAND_BACK_ACTION}
                handBackTarget={null}
                approveAction={APPROVE_ACTION}
                approve={(action) => advanceWorkflow(c.id, action.action, {})}
                approveHint={
                    notes.length > 0 ? 'Remove the notes to approve, or request a revision' : null
                }
                copy={COPY}
            />
        </div>
    );
}
